import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import PageTemplate from "@/components/page-template/PageTemplate";
import {
  Box,
  Card,
  Flex,
  Text,
  Heading,
  Button,
  TextField,
  Badge,
  Table,
  Grid,
} from "@radix-ui/themes";
import { Check, AlertCircle, Trash2, BarChart3, KeyRound } from "lucide-react";
import {
  integrationIcons,
  integrationBrandColors,
} from "@/components/workflow/IntegrationSelector";
import { integrationService } from "@/services/IntegrationService";
import { BaseService } from "@/services/BaseService";
import { DEFAULT_CHAT_MODEL } from "@/lib/anythinkChatTransport";
import { IntegrationConnection } from "@/types/breeze/Integration";

const AnythinkIcon = ({ size = 18 }: { size?: number }) => (
  <img
    src="/assets/anythink-icon-03.svg"
    alt="Anythink"
    width={size}
    height={size}
    className="anythink-icon"
    style={{ opacity: 0.9 }}
  />
);

const AI_PROVIDERS = [
  {
    id: "claude",
    name: "Claude (Anthropic)",
    description: "Recommended for sidebar",
  },
  { id: "openai", name: "OpenAI", description: "GPT models" },
  { id: "grok", name: "Grok (xAI)", description: "xAI models" },
  { id: "gemini", name: "Gemini (Google)", description: "Google AI models" },
];

// AI config service
class AiConfigService extends BaseService {
  getConfig = () => this.getRequest<any>("/integrations/ai/config");
  updateConfig = (data: any) =>
    this.putRequest<any, any>(
      "/integrations/ai/config",
      data,
      "AI settings updated",
      true,
      true,
    );
  getUsage = () => this.getRequest<any>("/integrations/ai/usage");
  getModels = () => this.getRequest<any[]>("/integrations/ai/models");
}
const aiConfigService = new AiConfigService();

export default function AISettingsPage() {
  const queryClient = useQueryClient();
  const [apiKeyInputs, setApiKeyInputs] = useState<Record<string, string>>({});

  // Fetch AI config from backend
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ["ai-config"],
    queryFn: () => aiConfigService.getConfig(),
  });

  // Fetch connections
  const { data: connections = [], isLoading: connectionsLoading } = useQuery({
    queryKey: ["ai-connections"],
    queryFn: async () => {
      const all = await integrationService.getConnections();
      return all.filter((c) =>
        ["claude", "openai", "grok"].includes(c.integration_definition_id),
      );
    },
  });

  // Fetch usage
  const { data: usage } = useQuery({
    queryKey: ["ai-usage"],
    queryFn: () => aiConfigService.getUsage(),
    refetchInterval: 30000,
  });

  // Fetch models dynamically
  const { data: discoveredModels = [] } = useQuery({
    queryKey: ["ai-models"],
    queryFn: async () => {
      const rawModels = await aiConfigService.getModels();
      return rawModels.map((m: any) => ({
        id: m.modelName || m.model_name,
        name: m.modelName || m.model_name,
        provider: m.provider,
        badge: m.badge,
        isByok: m.isByok ?? m.is_byok,
        description:
          m.description || (m.isByok ? "BYOK Model" : "Platform Model"),
      }));
    },
  });

  const platformModels = discoveredModels.filter((m: any) => !m.isByok);

  // Update config
  const { mutate: updateConfig } = useMutation({
    mutationFn: (data: any) => aiConfigService.updateConfig(data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["ai-config"] });
    },
  });

  // Save API key
  const { mutate: saveApiKey, isPending: isSaving } = useMutation({
    mutationFn: async ({
      provider,
      apiKey,
    }: {
      provider: string;
      apiKey: string;
    }) => {
      const existing = connections.find(
        (c) => c.integration_definition_id === provider,
      );
      if (existing) await integrationService.deleteConnection(existing.id);
      return integrationService.createApiKeyConnection({
        integration_definition_id: provider,
        name: `${provider} API Key`,
        api_key: apiKey,
        is_user_connection: false,
      });
    },
    onSuccess: (_, { provider }) => {
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] });
      setApiKeyInputs((prev) => ({ ...prev, [provider]: "" }));
    },
  });

  const { mutate: deleteConnection } = useMutation({
    mutationFn: (connectionId: string) =>
      integrationService.deleteConnection(connectionId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["ai-connections"] }),
  });

  const getConnection = (provider: string): IntegrationConnection | undefined =>
    connections.find((c) => c.integration_definition_id === provider);

  const aiMode = config?.ai_mode || "platform";
  const defaultModel = config?.ai_default_model || DEFAULT_CHAT_MODEL;
  const isLoading = configLoading || connectionsLoading;

  if (isLoading) {
    return (
      <PageTemplate title="AI Settings">
        <Text>Loading...</Text>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title="AI Settings"
      description="Configure AI model preferences and API keys for your organisation"
    >
      <Flex direction="column" gap="6">
        {/* ── Mode Selection ──────────────────────────────────────────── */}
        <Card size="3">
          <Box p="1">
            <Box mb="4">
              <Heading as="h2" size="4" weight="medium">
                AI Provider
              </Heading>
              <Text size="2" color="gray">
                Choose how your AI sidebar is powered
              </Text>
            </Box>

            <Flex gap="3">
              <Box
                onClick={() => updateConfig({ ai_mode: "platform" })}
                style={{
                  flex: 1,
                  padding: "16px",
                  borderRadius: "var(--radius-3)",
                  cursor: "pointer",
                  border:
                    aiMode === "platform"
                      ? "2px solid var(--accent-9)"
                      : "1px solid var(--gray-6)",
                  backgroundColor:
                    aiMode === "platform" ? "var(--accent-2)" : "transparent",
                }}
              >
                <Flex align="center" gap="2" mb="2">
                  <AnythinkIcon />
                  <Text size="3" weight="medium">
                    Anythink AI
                  </Text>
                  {aiMode === "platform" && (
                    <Badge color="green" size="1">
                      Active
                    </Badge>
                  )}
                </Flex>
                <Text size="2" color="gray">
                  Platform-managed. Uses your credit balance. No setup required.
                </Text>
              </Box>

              <Box
                onClick={() => updateConfig({ ai_mode: "byok" })}
                style={{
                  flex: 1,
                  padding: "16px",
                  borderRadius: "var(--radius-3)",
                  cursor: "pointer",
                  border:
                    aiMode === "byok"
                      ? "2px solid var(--green-9)"
                      : "1px solid var(--gray-6)",
                  backgroundColor:
                    aiMode === "byok" ? "var(--green-2)" : "transparent",
                }}
              >
                <Flex align="center" gap="2" mb="2">
                  <KeyRound size={18} />
                  <Text size="3" weight="medium">
                    Bring Your Own Key
                  </Text>
                  {aiMode === "byok" && (
                    <Badge color="green" size="1">
                      Active
                    </Badge>
                  )}
                </Flex>
                <Text size="2" color="gray">
                  Use your own API keys. Direct billing with providers. No
                  credit usage.
                </Text>
              </Box>
            </Flex>
          </Box>
        </Card>

        <Grid columns={{ initial: "1", md: "2" }} gap="6">
          {/* ── Left column: Model or API Keys ──────────────────────────── */}
          {aiMode === "platform" ? (
            <Card size="3">
              <Box p="1">
                <Box mb="4">
                  <Heading as="h2" size="4" weight="medium">
                    Default Model
                  </Heading>
                  <Text size="2" color="gray">
                    Choose the model for Anythink AI
                  </Text>
                </Box>

                <Flex direction="column" gap="2">
                  {platformModels.map((model: any) => (
                    <Box
                      key={model.id}
                      onClick={() =>
                        updateConfig({ ai_default_model: model.id })
                      }
                      style={{
                        padding: "12px 14px",
                        border:
                          defaultModel === model.id
                            ? "2px solid var(--accent-9)"
                            : "1px solid var(--gray-6)",
                        borderRadius: "var(--radius-3)",
                        cursor: "pointer",
                        backgroundColor:
                          defaultModel === model.id
                            ? "var(--accent-2)"
                            : "transparent",
                      }}
                    >
                      <Flex justify="between" align="center">
                        <Flex align="center" gap="2">
                          <Text size="2" weight="medium">
                            {model.name}
                          </Text>
                          {model.badge && (
                            <Badge size="1" variant="soft">
                              {model.badge}
                            </Badge>
                          )}
                          <Text size="1" color="gray">
                            {model.description}
                          </Text>
                        </Flex>
                        {defaultModel === model.id && (
                          <Check size={16} color="var(--accent-9)" />
                        )}
                      </Flex>
                    </Box>
                  ))}
                  {platformModels.length === 0 && (
                    <Text size="2" color="gray" align="center">
                      No platform models available
                    </Text>
                  )}
                </Flex>
              </Box>
            </Card>
          ) : (
            <Card size="3">
              <Box p="1">
                <Box mb="2">
                  <Heading as="h2" size="4" weight="medium">
                    API Keys
                  </Heading>
                  <Text size="2" color="gray">
                    Configure your provider API keys
                  </Text>
                </Box>

                <Box
                  mb="4"
                  p="3"
                  style={{
                    backgroundColor: "var(--blue-2)",
                    borderRadius: "var(--radius-2)",
                    border: "1px solid var(--blue-6)",
                  }}
                >
                  <Flex align="start" gap="2">
                    <AlertCircle
                      size={16}
                      color="var(--blue-9)"
                      style={{ marginTop: 2, flexShrink: 0 }}
                    />
                    <Text size="2" color="blue">
                      API keys are encrypted at rest. The AI sidebar will use
                      the Claude key when available. All usage is tracked
                      regardless of provider.
                    </Text>
                  </Flex>
                </Box>

                <Flex direction="column" gap="3">
                  {AI_PROVIDERS.map((provider) => {
                    const connection = getConnection(provider.id);
                    const inputValue = apiKeyInputs[provider.id] || "";

                    return (
                      <Box
                        key={provider.id}
                        p="3"
                        style={{
                          border: "1px solid var(--gray-6)",
                          borderRadius: "var(--radius-3)",
                        }}
                      >
                        <Flex
                          justify="between"
                          align="center"
                          mb={connection ? "0" : "2"}
                        >
                          <Flex align="center" gap="3">
                            {(() => {
                              const ProviderIcon =
                                integrationIcons[provider.id];
                              const brand = integrationBrandColors[provider.id];
                              return ProviderIcon ? (
                                <Flex
                                  align="center"
                                  justify="center"
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "6px",
                                    backgroundColor:
                                      brand?.bg || "var(--gray-8)",
                                  }}
                                >
                                  <Box
                                    style={{
                                      width: 16,
                                      height: 16,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <ProviderIcon />
                                  </Box>
                                </Flex>
                              ) : null;
                            })()}
                            <Box>
                              <Text size="2" weight="medium">
                                {provider.name}
                              </Text>
                              <Text
                                size="1"
                                color="gray"
                                style={{ display: "block" }}
                              >
                                {provider.description}
                              </Text>
                            </Box>
                          </Flex>
                          {connection && (
                            <Badge size="1" color="green">
                              <Check size={10} /> Connected
                            </Badge>
                          )}
                        </Flex>

                        {connection ? (
                          <Flex justify="between" align="center" mt="2">
                            <Text size="1" color="gray">
                              Connected{" "}
                              {new Date(
                                connection.connected_at,
                              ).toLocaleDateString()}
                            </Text>
                            <Button
                              size="1"
                              variant="ghost"
                              color="red"
                              onClick={() => deleteConnection(connection.id)}
                            >
                              <Trash2 size={12} /> Remove
                            </Button>
                          </Flex>
                        ) : (
                          <Flex gap="2">
                            <TextField.Root
                              style={{ flex: 1 }}
                              type="password"
                              placeholder={`${provider.name} API key`}
                              value={inputValue}
                              onChange={(e) =>
                                setApiKeyInputs((prev) => ({
                                  ...prev,
                                  [provider.id]: e.target.value,
                                }))
                              }
                            />
                            <Button
                              disabled={!inputValue.trim() || isSaving}
                              onClick={() =>
                                saveApiKey({
                                  provider: provider.id,
                                  apiKey: inputValue,
                                })
                              }
                            >
                              Save
                            </Button>
                          </Flex>
                        )}
                      </Box>
                    );
                  })}
                </Flex>
              </Box>
            </Card>
          )}

          {/* ── Right column: Usage ─────────────────────────────────────── */}
          <Card size="3">
            <Box p="1">
              <Box mb="4">
                <Heading as="h2" size="4" weight="medium">
                  Usage
                </Heading>
                <Text size="2" color="gray">
                  Token consumption (last 30 days)
                </Text>
              </Box>

              {usage ? (
                <Flex direction="column" gap="4">
                  <Box
                    style={{
                      padding: "16px",
                      backgroundColor: "var(--gray-a2)",
                      borderRadius: "var(--radius-3)",
                      border: "1px solid var(--gray-5)",
                    }}
                  >
                    <Flex justify="between" align="center" mb="2">
                      <Text size="2" weight="medium">
                        Usage to Date
                      </Text>
                      <Badge size="1" variant="soft">
                        {usage.since
                          ? `Since ${new Date(usage.since).toLocaleDateString()}`
                          : "Current Period"}
                      </Badge>
                    </Flex>

                    <Flex direction="column" gap="2">
                      <Flex justify="between" align="end">
                        <Box>
                          <Text
                            size="1"
                            color="gray"
                            style={{ display: "block" }}
                          >
                            Tokens Consumed
                          </Text>
                          <Text size="6" weight="bold">
                            {(
                              usage.consumed ||
                              usage.total_tokens ||
                              0
                            ).toLocaleString()}
                          </Text>
                        </Box>
                        <Box style={{ textAlign: "right" }}>
                          <Text
                            size="1"
                            color="gray"
                            style={{ display: "block" }}
                          >
                            Quota
                          </Text>
                          <Text size="4" weight="medium">
                            {usage.base_quota || usage.bundle_quota
                              ? (
                                  (usage.base_quota || 0) +
                                  (usage.bundle_quota || 0)
                                ).toLocaleString()
                              : "Unlimited"}
                          </Text>
                        </Box>
                      </Flex>

                      {(usage.base_quota || usage.bundle_quota) > 0 && (
                        <Box
                          style={{
                            height: 8,
                            width: "100%",
                            backgroundColor: "var(--gray-4)",
                            borderRadius: 4,
                            overflow: "hidden",
                            marginTop: 4,
                          }}
                        >
                          <Box
                            style={{
                              height: "100%",
                              width: `${Math.min(
                                100,
                                ((usage.consumed || usage.total_tokens || 0) /
                                  ((usage.base_quota || 0) +
                                    (usage.bundle_quota || 0))) *
                                  100,
                              )}%`,
                              backgroundColor:
                                (usage.consumed || usage.total_tokens || 0) /
                                  ((usage.base_quota || 0) +
                                    (usage.bundle_quota || 0)) >
                                0.9
                                  ? "var(--red-9)"
                                  : "var(--accent-9)",
                              transition: "width 0.5s ease-out",
                            }}
                          />
                        </Box>
                      )}
                    </Flex>
                  </Box>

                  {usage.by_model && usage.by_model.length > 0 && (
                    <Box mt="2">
                      <Text
                        size="2"
                        weight="medium"
                        mb="2"
                        style={{ display: "block" }}
                      >
                        Breakdown by Model
                      </Text>
                      <Table.Root size="1">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeaderCell>
                              Model
                            </Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>
                              Tokens
                            </Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell>
                              Mode
                            </Table.ColumnHeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {usage.by_model.map((m: any, i: number) => (
                            <Table.Row key={`${m.model}-${i}`}>
                              <Table.Cell>
                                <Text size="2">{m.model}</Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Text size="2">
                                  {(
                                    (m.input_tokens || 0) +
                                    (m.output_tokens || 0)
                                  ).toLocaleString()}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Badge
                                  size="1"
                                  variant="soft"
                                  color={m.mode === "byok" ? "green" : "orange"}
                                >
                                  {m.mode === "byok" ? "BYOK" : "Platform"}
                                </Badge>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  )}
                </Flex>
              ) : (
                <Flex direction="column" align="center" py="4">
                  <BarChart3 size={32} color="var(--gray-8)" strokeWidth={1} />
                  <Text size="2" color="gray" mt="2">
                    No usage recorded yet
                  </Text>
                  <Text size="1" color="gray">
                    Start a conversation in the AI sidebar to see token usage
                  </Text>
                </Flex>
              )}
            </Box>
          </Card>
        </Grid>
      </Flex>
    </PageTemplate>
  );
}
